// src/pages/produccion/Produccion.jsx
// Captura Diaria de Producción + Historial, en una sola pantalla con
// pestañas (mismo patrón que Merma.jsx).
import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';

const LINEAS = ['Granola', 'Barras'];
const AREAS = ['Mezclado', 'Horneado', 'Empaque'];
const TURNOS = ['Matutino', 'Nocturno'];

const RECETAS_POR_LINEA = {
  Granola: [
    'Granola Original', 'Granola Ligera', 'Granola Chocolate', 'Granola Vainilla', 'Granola Maple',
    'Cereal Arándano', 'Cereal Quinoa', 'Cereal Chocolate', 'Cereal Maple',
  ],
  Barras: ['Barra Original', 'Barra Ligera', 'Barra Chocolate'],
};

/** Verbo del área para la etiqueta "Carros ___" (mezclados/horneados/empacados) */
const VERBO_AREA = { Mezclado: 'mezclados', Horneado: 'horneados', Empaque: 'empacados' };

/** Calcula horas entre dos horarios HH:MM, soportando turnos que cruzan medianoche (ej. 21:00 a 05:00) */
function horasEntre(inicio, fin) {
  if (!inicio || !fin) return 0;
  const [hI, mI] = inicio.split(':').map(Number);
  const [hF, mF] = fin.split(':').map(Number);
  let minutos = (hF * 60 + mF) - (hI * 60 + mI);
  if (minutos < 0) minutos += 24 * 60; // cruzó medianoche
  return Math.round((minutos / 60) * 100) / 100;
}

const filaVacia = () => ({ descripcion: '', inicio: '', fin: '', tiempo_horas: '' });
const grupoComidaVacio = () => ({ inicio: '', fin: '', personas: '' });
const recetaVacia = (linea) => ({ receta: RECETAS_POR_LINEA[linea][0], carros_prog: '', carros_prod: '', kg: '' });

const formVacio = () => ({
  fecha: new Date().toISOString().slice(0, 10),
  linea: 'Granola',
  area: 'Mezclado',
  turno: 'Matutino',
  asistencia: '',
  asistencia_formado: '',
  no_hornos: '',
  horario_inicio: '',
  horario_fin: '',
  produccion_inicio: '',
  produccion_fin: '',
  limpieza_inicio: '',
  limpieza_fin: '',
  comentarios: '',
});

export default function Produccion() {
  const [tab, setTab] = useState('captura');
  return (
    <div style={styles.paginaContenedor}>
      <h1 style={styles.tituloPagina}>Producción Diaria</h1>
      <div style={styles.tabBar}>
        <button onClick={() => setTab('captura')} style={{ ...styles.tabBtn, ...(tab === 'captura' ? styles.tabBtnActivo : {}) }}>📝 Captura</button>
        <button onClick={() => setTab('historial')} style={{ ...styles.tabBtn, ...(tab === 'historial' ? styles.tabBtnActivo : {}) }}>📋 Historial</button>
      </div>
      {tab === 'captura' ? <TabCaptura /> : <TabHistorial />}
    </div>
  );
}

function TabCaptura() {
  const [form, setForm] = useState(formVacio());
  const [recetas, setRecetas] = useState([recetaVacia('Granola')]);
  const [comidaGrupos, setComidaGrupos] = useState([grupoComidaVacio()]);
  const [paros, setParos] = useState([filaVacia()]);
  const [capacitaciones, setCapacitaciones] = useState([filaVacia()]);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [kgPorCarro, setKgPorCarro] = useState({}); // { 'Granola Original': 92.041, ... }

  useEffect(() => {
    api.get('/api/produccion/config/kg-por-carro')
      .then((res) => {
        const mapa = {};
        res.data.data.forEach((r) => { mapa[r.receta] = parseFloat(r.kg_pt_x_carro); });
        setKgPorCarro(mapa);
      })
      .catch((err) => console.error('No se pudo cargar Kg por Carro:', err));
  }, []);

  function set(campo, valor) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  /** Al cambiar el inicio de Limpieza, el fin de Producción se llena solo */
  function handleLimpiezaInicio(valor) {
    setForm((prev) => ({ ...prev, limpieza_inicio: valor, produccion_fin: valor }));
  }

  function handleCambiarLinea(linea) {
    setForm((prev) => ({ ...prev, linea }));
    setRecetas([recetaVacia(linea)]); // al cambiar de línea, reinicia la lista de recetas
  }

  // --- Recetas dinámicas (antes receta_1/receta_2 fijas) ---
  function actualizarReceta(i, campo, valor) {
    setRecetas((prev) => {
      const copia = [...prev];
      copia[i] = { ...copia[i], [campo]: valor };
      // Auto-calcula Kg = Carros producidos × Kg por Carro de la receta
      const kgCarro = kgPorCarro[copia[i].receta];
      if ((campo === 'carros_prod' || campo === 'receta') && kgCarro && copia[i].carros_prod) {
        copia[i].kg = Math.round(parseFloat(copia[i].carros_prod) * kgCarro * 100) / 100;
      }
      return copia;
    });
  }
  function agregarReceta() { setRecetas((prev) => [...prev, recetaVacia(form.linea)]); }
  function quitarReceta(i) { setRecetas((prev) => prev.filter((_, idx) => idx !== i)); }

  // --- Grupos de comida ---
  function actualizarGrupoComida(i, campo, valor) {
    setComidaGrupos((prev) => {
      const copia = [...prev];
      copia[i] = { ...copia[i], [campo]: valor };
      return copia;
    });
  }
  function agregarGrupoComida() { setComidaGrupos((prev) => [...prev, grupoComidaVacio()]); }
  function quitarGrupoComida(i) { setComidaGrupos((prev) => prev.filter((_, idx) => idx !== i)); }

  // --- Filas de Tiempos Muertos ---
  function actualizarParo(i, campo, valor) {
    setParos((prev) => {
      const copia = [...prev];
      copia[i] = { ...copia[i], [campo]: valor };
      if (campo === 'inicio' || campo === 'fin') {
        copia[i].tiempo_horas = horasEntre(copia[i].inicio, copia[i].fin) || '';
      }
      return copia;
    });
  }
  function agregarParo() { setParos((prev) => [...prev, filaVacia()]); }
  function quitarParo(i) { setParos((prev) => prev.filter((_, idx) => idx !== i)); }

  // --- Filas de Capacitaciones ---
  function actualizarCapacitacion(i, campo, valor) {
    setCapacitaciones((prev) => {
      const copia = [...prev];
      copia[i] = { ...copia[i], [campo]: valor };
      if (campo === 'inicio' || campo === 'fin') {
        copia[i].tiempo_horas = horasEntre(copia[i].inicio, copia[i].fin) || '';
      }
      return copia;
    });
  }
  function agregarCapacitacion() { setCapacitaciones((prev) => [...prev, filaVacia()]); }
  function quitarCapacitacion(i) { setCapacitaciones((prev) => prev.filter((_, idx) => idx !== i)); }

  const totalParoHoras = paros.reduce((s, p) => s + (parseFloat(p.tiempo_horas) || 0), 0);
  const totalCapacitacionHoras = capacitaciones.reduce((s, c) => s + (parseFloat(c.tiempo_horas) || 0), 0);
  const esFormadoBarras = form.linea === 'Barras' && form.area === 'Mezclado';

  async function handleGuardar() {
    try {
      setGuardando(true);
      setMensaje(null);
      const payload = {
        ...form,
        recetas: recetas.filter((r) => r.receta),
        comidaGrupos: comidaGrupos.filter((g) => g.inicio || g.fin || g.personas),
        paros: paros.filter((p) => p.descripcion || p.tiempo_horas),
        capacitaciones: capacitaciones.filter((c) => c.descripcion || c.tiempo_horas),
      };
      await api.post('/api/produccion/diario', payload);
      setMensaje({ tipo: 'ok', texto: '✓ Captura guardada correctamente.' });
      setForm(formVacio());
      setRecetas([recetaVacia('Granola')]);
      setComidaGrupos([grupoComidaVacio()]);
      setParos([filaVacia()]);
      setCapacitaciones([filaVacia()]);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div style={styles.contenedor}>
      <p style={styles.subtitulo}>{form.area} {form.linea} — un renglón por área/línea/turno.</p>

      {/* Receta a producir */}
      <Seccion titulo="Receta a Producir">
        <div style={styles.grid3}>
          <Campo label="Fecha">
            <input type="date" value={form.fecha} onChange={(e) => set('fecha', e.target.value)} style={styles.input} />
          </Campo>
          <Campo label="Línea">
            <select value={form.linea} onChange={(e) => handleCambiarLinea(e.target.value)} style={styles.input}>
              {LINEAS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Campo>
          <Campo label="Área">
            <select value={form.area} onChange={(e) => set('area', e.target.value)} style={styles.input}>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Campo>
          <Campo label="Turno">
            <select value={form.turno} onChange={(e) => set('turno', e.target.value)} style={styles.input}>
              {TURNOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Campo>
          <Campo label={esFormadoBarras ? 'Asistencia Mezclado' : 'Asistencia (personas)'}>
            <input type="number" value={form.asistencia} onChange={(e) => set('asistencia', e.target.value)} style={styles.input} />
          </Campo>
          {esFormadoBarras && (
            <Campo label="Asistencia Formado">
              <input type="number" value={form.asistencia_formado} onChange={(e) => set('asistencia_formado', e.target.value)} style={styles.input} />
            </Campo>
          )}
          {form.area === 'Horneado' && (
            <Campo label="No. de Hornos">
              <input type="number" value={form.no_hornos} onChange={(e) => set('no_hornos', e.target.value)} style={styles.input} />
            </Campo>
          )}
        </div>
      </Seccion>

      {/* Producción — recetas dinámicas */}
      <Seccion titulo="Producción">
        {recetas.map((r, i) => {
          const pctCumplimiento = r.carros_prog
            ? Math.round(((parseFloat(r.carros_prod) || 0) / parseFloat(r.carros_prog)) * 1000) / 10
            : null;
          const asistenciaBase = esFormadoBarras ? form.asistencia_formado : form.asistencia;
          const kgPorPersona = asistenciaBase
            ? Math.round(((parseFloat(r.kg) || 0) / parseFloat(asistenciaBase)) * 100) / 100
            : null;
          return (
            <div key={i} style={styles.bloqueReceta}>
              <div style={styles.bloqueRecetaHeader}>
                <span style={styles.bloqueRecetaTitulo}>Receta {i + 1}</span>
                {recetas.length > 1 && (
                  <button onClick={() => quitarReceta(i)} style={styles.botonQuitarFila}>✕</button>
                )}
              </div>
              <div style={styles.grid4}>
                <Campo label="Receta">
                  <select value={r.receta} onChange={(e) => actualizarReceta(i, 'receta', e.target.value)} style={styles.input}>
                    {RECETAS_POR_LINEA[form.linea].map((rec) => <option key={rec} value={rec}>{rec}</option>)}
                  </select>
                </Campo>
                <Campo label="Carros programados">
                  <input type="number" value={r.carros_prog} onChange={(e) => actualizarReceta(i, 'carros_prog', e.target.value)} style={styles.input} />
                </Campo>
                <Campo label={`Carros ${VERBO_AREA[form.area]}`}>
                  <input type="number" value={r.carros_prod} onChange={(e) => actualizarReceta(i, 'carros_prod', e.target.value)} style={styles.input} />
                </Campo>
                <Campo label="Kg producidos (auto — editable)">
                  <input type="number" step="0.01" value={r.kg} onChange={(e) => actualizarReceta(i, 'kg', e.target.value)} style={styles.input} />
                </Campo>
              </div>
              {(pctCumplimiento !== null || kgPorPersona !== null) && (
                <div style={styles.calculados}>
                  {pctCumplimiento !== null && <span>% Cumplimiento: <strong>{pctCumplimiento}%</strong></span>}
                  {kgPorPersona !== null && <span>Kg por persona: <strong>{kgPorPersona}</strong></span>}
                </div>
              )}
            </div>
          );
        })}
        <button onClick={agregarReceta} style={styles.botonAgregarSecundario}>
          + Agregar receta (si se produjo más de una en el turno)
        </button>
      </Seccion>

      {/* Tiempos de referencia */}
      <Seccion titulo="Tiempos (referencia)">
        <div style={styles.gridTiempos}>
          <CajaTiempo>
            <ParHorario label="Horario" inicio={form.horario_inicio} fin={form.horario_fin} onInicio={(v) => set('horario_inicio', v)} onFin={(v) => set('horario_fin', v)} />
          </CajaTiempo>
          <CajaTiempo>
            <label style={styles.label}>Producción</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input type="time" value={form.produccion_inicio} onChange={(e) => set('produccion_inicio', e.target.value)} style={styles.input} />
              <input
                type="time"
                value={form.produccion_fin}
                disabled
                style={{ ...styles.input, background: '#fff', color: '#8A7690', border: '1px dashed #C9BFC4' }}
                title="Se llena automático con el inicio de Limpieza"
              />
            </div>
            <div style={styles.horasCalc}>{horasEntre(form.produccion_inicio, form.produccion_fin).toFixed(2)} hrs · fin automático</div>
          </CajaTiempo>
          <CajaTiempo>
            <label style={styles.label}>Limpieza</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input type="time" value={form.limpieza_inicio} onChange={(e) => handleLimpiezaInicio(e.target.value)} style={styles.input} />
              <input type="time" value={form.limpieza_fin} onChange={(e) => set('limpieza_fin', e.target.value)} style={styles.input} />
            </div>
            <div style={styles.horasCalc}>{horasEntre(form.limpieza_inicio, form.limpieza_fin).toFixed(2)} hrs</div>
          </CajaTiempo>
        </div>
      </Seccion>

      {/* Comida por grupos */}
      <Seccion titulo="Comida por Grupos">
        <div style={styles.tablaHead}>
          <span style={{ flex: 1 }}>Inicio</span>
          <span style={{ flex: 1 }}>Fin</span>
          <span style={{ flex: 1 }}>Personas</span>
          <span style={{ width: '28px' }} />
        </div>
        {comidaGrupos.map((g, i) => (
          <div key={i} style={styles.tablaFila}>
            <input type="time" value={g.inicio} onChange={(e) => actualizarGrupoComida(i, 'inicio', e.target.value)} style={{ ...styles.inputTabla, flex: 1 }} />
            <input type="time" value={g.fin} onChange={(e) => actualizarGrupoComida(i, 'fin', e.target.value)} style={{ ...styles.inputTabla, flex: 1 }} />
            <input type="number" value={g.personas} onChange={(e) => actualizarGrupoComida(i, 'personas', e.target.value)} placeholder="Ej. 8" style={{ ...styles.inputTabla, flex: 1 }} />
            {comidaGrupos.length > 1 && (
              <button onClick={() => quitarGrupoComida(i)} style={styles.botonQuitarFila}>✕</button>
            )}
          </div>
        ))}
        <button onClick={agregarGrupoComida} style={styles.botonAgregarFila}>+ Agregar grupo de comida</button>
        <p style={styles.notaGrupos}>Si todos comen juntos, deja un solo grupo. Si comen por turnos (2 o 3 grupos), agrega uno por cada horario.</p>
      </Seccion>

      {/* Tiempos muertos (tabla dinámica) */}
      <Seccion titulo="Tiempos Muertos">
        <TablaDinamica
          filas={paros}
          onActualizar={actualizarParo}
          onAgregar={agregarParo}
          onQuitar={quitarParo}
          textoAgregar="+ Agregar paro"
        />
        <div style={styles.totalHoras}>Total Horas de Paro: <strong>{totalParoHoras.toFixed(2)}</strong></div>
      </Seccion>

      {/* Capacitaciones (tabla dinámica) */}
      <Seccion titulo="Capacitaciones o Charlas">
        <TablaDinamica
          filas={capacitaciones}
          onActualizar={actualizarCapacitacion}
          onAgregar={agregarCapacitacion}
          onQuitar={quitarCapacitacion}
          textoAgregar="+ Agregar capacitación"
        />
        <div style={styles.totalHoras}>Total Horas de Capacitación: <strong>{totalCapacitacionHoras.toFixed(2)}</strong></div>
      </Seccion>

      {/* Comentarios */}
      <Seccion titulo="Comentarios y Observaciones Relevantes">
        <textarea
          value={form.comentarios}
          onChange={(e) => set('comentarios', e.target.value)}
          rows={3}
          placeholder="Ej. 14 carros y 15 charolas..."
          style={{ ...styles.input, width: '100%', resize: 'vertical' }}
        />
      </Seccion>

      {mensaje && (
        <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>
          {mensaje.texto}
        </div>
      )}

      <button onClick={handleGuardar} disabled={guardando} style={styles.botonCapturar}>
        {guardando ? 'Guardando...' : 'Capturar'}
      </button>
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div style={styles.tarjeta}>
      <div style={styles.seccionTitulo}>{titulo}</div>
      {children}
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function CajaTiempo({ children }) {
  return <div style={styles.cajaTiempo}>{children}</div>;
}

function ParHorario({ label, inicio, fin, onInicio, onFin }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input type="time" value={inicio} onChange={(e) => onInicio(e.target.value)} style={styles.input} />
        <input type="time" value={fin} onChange={(e) => onFin(e.target.value)} style={styles.input} />
      </div>
      <div style={styles.horasCalc}>{horasEntre(inicio, fin).toFixed(2)} hrs</div>
    </div>
  );
}

function TablaDinamica({ filas, onActualizar, onAgregar, onQuitar, textoAgregar }) {
  return (
    <div>
      <div style={styles.tablaHead}>
        <span style={{ flex: 2 }}>Descripción</span>
        <span style={{ flex: 1 }}>Inicio</span>
        <span style={{ flex: 1 }}>Fin</span>
        <span style={{ flex: 1 }}>Tiempo (hrs)</span>
        <span style={{ width: '28px' }} />
      </div>
      {filas.map((f, i) => (
        <div key={i} style={styles.tablaFila}>
          <input
            type="text"
            value={f.descripcion}
            onChange={(e) => onActualizar(i, 'descripcion', e.target.value)}
            placeholder="Ej. Falla en selladora"
            style={{ ...styles.inputTabla, flex: 2 }}
          />
          <input type="time" value={f.inicio} onChange={(e) => onActualizar(i, 'inicio', e.target.value)} style={{ ...styles.inputTabla, flex: 1 }} />
          <input type="time" value={f.fin} onChange={(e) => onActualizar(i, 'fin', e.target.value)} style={{ ...styles.inputTabla, flex: 1 }} />
          <input
            type="number" step="0.01"
            value={f.tiempo_horas}
            onChange={(e) => onActualizar(i, 'tiempo_horas', e.target.value)}
            style={{ ...styles.inputTabla, flex: 1 }}
          />
          <button onClick={() => onQuitar(i)} style={styles.botonQuitarFila}>✕</button>
        </div>
      ))}
      <button onClick={onAgregar} style={styles.botonAgregarFila}>{textoAgregar}</button>
    </div>
  );
}

const MESES = [
  ['01', 'Enero'], ['02', 'Febrero'], ['03', 'Marzo'], ['04', 'Abril'], ['05', 'Mayo'], ['06', 'Junio'],
  ['07', 'Julio'], ['08', 'Agosto'], ['09', 'Septiembre'], ['10', 'Octubre'], ['11', 'Noviembre'], ['12', 'Diciembre'],
];
function ultimoDiaDelMes(anio, mes) {
  return new Date(anio, parseInt(mes), 0).getDate();
}

function TabHistorial() {
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');
  const [fMes, setFMes] = useState('');
  const [fAnio, setFAnio] = useState(new Date().getFullYear());
  const [fLinea, setFLinea] = useState('');
  const [fArea, setFArea] = useState('');
  const [fTurno, setFTurno] = useState('');

  async function cargar() {
    try {
      setCargando(true);
      const params = {};
      if (fDesde) params.desde = fDesde;
      if (fHasta) params.hasta = fHasta;
      if (fLinea) params.linea = fLinea;
      if (fArea) params.area = fArea;
      if (fTurno) params.turno = fTurno;
      const res = await api.get('/api/produccion/diario', { params });
      setRegistros(res.data.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar el historial.');
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleMesChange(mes) {
    setFMes(mes);
    if (mes) {
      setFDesde(`${fAnio}-${mes}-01`);
      setFHasta(`${fAnio}-${mes}-${String(ultimoDiaDelMes(fAnio, mes)).padStart(2, '0')}`);
    }
  }
  function limpiarFiltros() {
    setFDesde(''); setFHasta(''); setFMes(''); setFLinea(''); setFArea(''); setFTurno('');
  }

  function exportarExcel() {
    const filas = registros.map((r) => ({
      Fecha: new Date(r.fecha).toLocaleDateString('es-MX'),
      Línea: r.linea,
      Área: r.area,
      Turno: r.turno,
      Recetas: r.recetas_resumen,
      'Carros Total': r.carros_total,
      'Kg Total': r.kg_total,
      Asistencia: r.asistencia ?? '',
      'Asistencia Formado': r.asistencia_formado ?? '',
      'Personas en Comida': r.comida_personas_total,
      'Horas de Paro': r.tiempo_paro_horas,
      'Horas de Capacitación': r.tiempo_capacitacion_horas,
      Comentarios: r.comentarios || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [
      { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 40 },
      { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 18 }, { wch: 40 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Producción');
    XLSX.writeFile(wb, `dasavena_produccion_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <div>
      <div style={styles.headerFila}>
        <p style={styles.subtitulo}>Todas las capturas — filtra y descarga.</p>
        <button onClick={exportarExcel} disabled={registros.length === 0} style={styles.botonExcel}>⬇ Descargar Excel</button>
      </div>

      <div style={styles.filtrosBar}>
        <FiltroFecha label="Desde" value={fDesde} onChange={(v) => { setFDesde(v); setFMes(''); }} />
        <FiltroFecha label="Hasta" value={fHasta} onChange={(v) => { setFHasta(v); setFMes(''); }} />
        <FiltroSel label="Mes" value={fMes} onChange={handleMesChange} options={[['', '—'], ...MESES]} />
        <div style={styles.filtroGrupo}>
          <label style={styles.filtroLabel}>Año</label>
          <input type="number" value={fAnio} onChange={(e) => setFAnio(e.target.value)} style={{ ...styles.filtroInput, width: '80px' }} />
        </div>
        <FiltroSel label="Línea" value={fLinea} onChange={setFLinea} options={[['', 'Todas'], ...LINEAS.map((l) => [l, l])]} />
        <FiltroSel label="Área" value={fArea} onChange={setFArea} options={[['', 'Todas'], ...AREAS.map((a) => [a, a])]} />
        <FiltroSel label="Turno" value={fTurno} onChange={setFTurno} options={[['', 'Todos'], ...TURNOS.map((t) => [t, t])]} />
        <button onClick={cargar} style={styles.botonBuscar}>Buscar</button>
        <button onClick={limpiarFiltros} style={styles.botonLimpiar}>Limpiar</button>
      </div>

      <div style={styles.resultadoInfo}>{registros.length} registros</div>

      {cargando ? (
        <div>Cargando...</div>
      ) : error ? (
        <div style={{ color: '#B23A48' }}>{error}</div>
      ) : (
        <div style={styles.tablaScroll}>
          <table style={styles.tabla}>
            <thead>
              <tr>
                <th style={styles.th}>Fecha</th><th style={styles.th}>Línea</th><th style={styles.th}>Área</th>
                <th style={styles.th}>Turno</th><th style={styles.th}>Recetas</th><th style={styles.th}>Kg Total</th>
                <th style={styles.th}>Asistencia</th><th style={styles.th}>Asist. Formado</th>
                <th style={styles.th}>Personas Comida</th><th style={styles.th}>Hrs Paro</th>
                <th style={styles.th}>Hrs Capacitación</th><th style={styles.th}>Comentarios</th>
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>{new Date(r.fecha).toLocaleDateString('es-MX')}</td>
                  <td style={styles.td}>{r.linea}</td>
                  <td style={styles.td}>{r.area}</td>
                  <td style={styles.td}>{r.turno}</td>
                  <td style={{ ...styles.td, maxWidth: 260, whiteSpace: 'normal' }}>{r.recetas_resumen || '—'}</td>
                  <td style={{ ...styles.td, fontWeight: 700 }}>{parseFloat(r.kg_total).toFixed(2)}</td>
                  <td style={styles.td}>{r.asistencia ?? '—'}</td>
                  <td style={styles.td}>{r.asistencia_formado ?? '—'}</td>
                  <td style={styles.td}>{r.comida_personas_total || '—'}</td>
                  <td style={styles.td}>{r.tiempo_paro_horas}</td>
                  <td style={styles.td}>{r.tiempo_capacitacion_horas}</td>
                  <td style={{ ...styles.td, maxWidth: 220, whiteSpace: 'normal' }}>{r.comentarios || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {registros.length === 0 && <p style={{ padding: 16, color: '#8A7690', fontSize: 12 }}>Sin registros para este filtro.</p>}
        </div>
      )}
    </div>
  );
}

function FiltroSel({ label, value, onChange, options }) {
  return (
    <div style={styles.filtroGrupo}>
      <label style={styles.filtroLabel}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.filtroInput}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function FiltroFecha({ label, value, onChange }) {
  return (
    <div style={styles.filtroGrupo}>
      <label style={styles.filtroLabel}>{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} style={styles.filtroInput} />
    </div>
  );
}

const styles = {
  paginaContenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42' },
  tituloPagina: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '14px' },
  tabBar: { display: 'flex', gap: '6px', borderBottom: '2px solid #E6DDD2', marginBottom: '20px' },
  tabBtn: { padding: '10px 20px', fontSize: '13px', fontWeight: 600, color: '#8A7690', background: 'none', border: 'none', borderBottom: '3px solid transparent', cursor: 'pointer', marginBottom: '-2px' },
  tabBtnActivo: { color: '#4A1E5C', borderBottom: '3px solid #4A1E5C' },
  headerFila: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
  botonExcel: { padding: '8px 16px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#3E7C5A', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  filtrosBar: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' },
  filtroGrupo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filtroLabel: { fontSize: '10px', color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px' },
  filtroInput: { padding: '6px 8px', fontSize: '12px', border: '1px solid #E6DDD2', borderRadius: '6px', fontFamily: 'Inter, sans-serif' },
  botonBuscar: { padding: '7px 16px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#4A1E5C', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  botonLimpiar: { padding: '7px 14px', fontSize: '12px', color: '#8A7690', background: 'transparent', border: '1px solid #E6DDD2', borderRadius: '6px', cursor: 'pointer' },
  resultadoInfo: { fontSize: '12px', color: '#8A7690', marginBottom: '10px' },
  tablaScroll: { overflowX: 'auto', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '10px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #E6DDD2', color: '#6E4F80', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td: { padding: '6px 10px', borderBottom: '1px solid #F0E9E0', whiteSpace: 'nowrap' },
  contenedor: { fontFamily: 'Inter, sans-serif', color: '#3A2E42', maxWidth: '820px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '2px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.4px' },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', marginBottom: '14px' },
  seccionTitulo: { fontSize: '11px', fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #F0E9E0' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  grid4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' },
  gridTiempos: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  cajaTiempo: { background: '#FAF6F0', border: '1px solid #F0E9E0', borderRadius: '9px', padding: '12px' },
  label: { display: 'block', fontSize: '11px', color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' },
  input: { width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #E6DDD2', borderRadius: '7px', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' },
  horasCalc: { fontSize: '11px', color: '#6E4F80', marginTop: '4px' },
  calculados: { display: 'flex', gap: '18px', fontSize: '12px', color: '#4A1E5C', background: '#F7EDD6', padding: '8px 12px', borderRadius: '7px', marginTop: '10px' },
  bloqueReceta: { border: '1px solid #F0E9E0', borderRadius: '9px', padding: '14px', marginBottom: '12px', background: '#FAF6F0' },
  bloqueRecetaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  bloqueRecetaTitulo: { fontSize: '11px', fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: '0.4px' },
  notaGrupos: { fontSize: '11px', color: '#8A7690', marginTop: '8px', fontStyle: 'italic' },
  botonAgregarSecundario: { marginTop: '12px', fontSize: '12px', color: '#6E4F80', background: '#EFE9F3', border: '1px dashed #A98BB8', borderRadius: '7px', padding: '8px 12px', cursor: 'pointer', width: '100%' },
  botonQuitarSecundario: { marginTop: '10px', fontSize: '11px', color: '#B23A48', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' },
  tablaHead: { display: 'flex', gap: '6px', fontSize: '10px', color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px', paddingLeft: '2px' },
  tablaFila: { display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' },
  inputTabla: { padding: '6px 8px', fontSize: '12px', border: '1px solid #E6DDD2', borderRadius: '6px', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' },
  botonQuitarFila: { width: '28px', height: '28px', border: 'none', background: '#F7E5E7', color: '#B23A48', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' },
  botonAgregarFila: { marginTop: '4px', fontSize: '12px', color: '#6E4F80', background: 'none', border: '1px dashed #E6DDD2', borderRadius: '7px', padding: '7px 12px', cursor: 'pointer', width: '100%' },
  totalHoras: { marginTop: '10px', fontSize: '13px', color: '#4A1E5C', background: '#F7EDD6', padding: '8px 12px', borderRadius: '7px' },
  mensaje: { fontSize: '13px', margin: '10px 0', fontWeight: 600 },
  botonCapturar: { width: '100%', padding: '14px', fontSize: '15px', fontWeight: 700, color: '#fff', background: '#4A1E5C', border: 'none', borderRadius: '9px', cursor: 'pointer', marginTop: '6px' },
};